import { isBefore } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import {
  noDeadlineSectionID,
  overdueSectionID,
} from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatDateKey, parseDateKey } from 'shared/utils/format-date-key';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
};

export const useDeadlineDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const dateSections = Array.from(
      tasks.reduce<Set<ID>>((acc, task) => {
        if (!task.deadlineDeadline) {
          acc.add(noDeadlineSectionID);
          return acc;
        }
        if (isBefore(task.deadlineDeadline, today)) {
          acc.add(overdueSectionID);
          return acc;
        }
        const dateKey = formatDateKey(task.deadlineDeadline!);
        acc.add(dateKey);
        return acc;
      }, new Set<ID>()),
    );

    if (showEmptyGroups) {
      if (!dateSections.includes(noDeadlineSectionID)) {
        dateSections.push(noDeadlineSectionID);
      }
      if (!dateSections.includes(overdueSectionID)) {
        dateSections.push(overdueSectionID);
      }
    }

    dateSections.sort((a, b) =>
      // Overdue section to the top
      a === overdueSectionID
        ? -1
        : b === overdueSectionID
          ? 1
          : // No Deadline section to the bottom
            a === noDeadlineSectionID
            ? 1
            : b === noDeadlineSectionID
              ? -1
              : parseDateKey(a).getTime() - parseDateKey(b).getTime(),
    );
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? dateSections
            .map((dateSection) => customOrder?.[dateSection] ?? [])
            .flat()
        : [];
    const treeBase: Record<ID, TreeItem> = showEmptyGroups
      ? {
          [overdueSectionID]: {
            id: overdueSectionID,
            type: TreeItemTypes.Date,
            name: t('general.sections.overdue.title'),
            children: [],
            isFolder: true,
          },
        }
      : {};

    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
      allDayFirst: true,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      if (!task.deadlineDeadline) {
        acc[noDeadlineSectionID] = acc[noDeadlineSectionID] ?? {
          id: noDeadlineSectionID,
          type: TreeItemTypes.Date,
          name: t('general.labels.no-deadline'),
          children: [],
          isFolder: true,
        };
        acc[noDeadlineSectionID].children?.push(task.id);
        acc[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };
        return acc;
      }

      // Add overdue section if there are overdue tasks
      if (isBefore(task.deadlineDeadline, today)) {
        acc[overdueSectionID] = acc[overdueSectionID] ?? {
          id: overdueSectionID,
          type: TreeItemTypes.Date,
          name: t('general.sections.overdue.title'),
          children: [],
          isFolder: true,
        };
        acc[overdueSectionID].children?.push(task.id);
        acc[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };
        return acc;
      }

      const dateKey = formatDateKey(task.deadlineDeadline!);
      acc[dateKey] = acc[dateKey] ?? {
        id: dateKey,
        type: TreeItemTypes.Date,
        name: formatDateKey(task.deadlineDeadline!),
        children: [],
        isFolder: true,
      };
      acc[dateKey].children?.push(task.id);
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };

      return acc;
    }, treeBase);

    if (showEmptyGroups) {
      allTreeItems[noDeadlineSectionID] = allTreeItems[noDeadlineSectionID] ?? {
        id: noDeadlineSectionID,
        type: TreeItemTypes.Date,
        name: t('general.labels.no-deadline'),
        children: [],
        isFolder: true,
      };
    }

    // Add create task rows
    dateSections.forEach((dateSectionKey) => {
      // overdue does not need a create-task row
      if (dateSectionKey === overdueSectionID) {
        return;
      }

      const newTaskItemId = `${dateSectionKey}-new-task`;
      allTreeItems[dateSectionKey]?.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: 'New Task',
        type: TreeItemTypes.NewTask,
      };
    });

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: dateSections,
      },
      ...allTreeItems,
    };
  }, [
    customOrder,
    sortingMode,
    t,
    tasks,
    today,
    weekStartsOn,
    showEmptyGroups,
  ]);
};
